// Data Health issue registry — presentation + resolution taxonomy.
//
// The BACKEND owns the semantic registry (category / scored / entity / count)
// in api/views/data_health.py; it arrives on `summary.issues[]`. The frontend
// keeps only presentation (icon / tone) and the *resolution* wiring here: how
// each issue is meant to be fixed, and — for the fixable ones — which canonical
// bulk-update endpoint the fix routes through.
//
// Every inline fix and instant toggle routes through a bulk-update on the
// owning ViewSet (Item / ItemLocation / Supplier / Location / Inventory) — Data
// Health hosts no fix endpoint of its own. Each def declares the target viewset (URL
// slug), the wire field on that serializer, and (for instant actions) the fixed
// value. The save flow assembles `{items:[{id, fields:{[fieldName]: value}}]}`
// and POSTs `/api/{viewset}/bulk-update/`. The indirection buys PO-impact
// confirmation, currency-change gating, SCD2 history with a shared `now`,
// batched validation, and Shopify sync for free — see api/views/viewsets.py.

// ---------------------------------------------------------------------------
// Resolution taxonomy — how an issue is resolved, from the merchant's POV.
// Derived from `actionType` (below) via `resolutionOf` so there is ONE source
// of truth per def, not a parallel field to keep in sync.
//
//   inline        — edit a field in the detail grid (generic bulk-update).
//   action        — a per-row action button: an instant toggle/activate, a
//                   channel-product create, a stale-placement remove.
//   bespoke       — a dedicated component owns the fix (the duplicate-review
//                   card; the supplier→ERP link dialog), writing through its
//                   own API, not the generic bulk-update.
//   deep-link     — fixed on another in-app screen, but Data Health hands you
//                   there. Two shapes: a per-row button that opens the target
//                   with the row's context (order rules → the Supplier catalog
//                   screen), and a whole-CARD button for an issue with no rows
//                   to show at all (`page-link` — the backend counts it from an
//                   engine, so there is no detail grid to put buttons in).
//                   No bulk-update from here, so no fix wiring either.
//   external      — resolvable, but NOT here and with no in-app button: the
//                   card copy names where (the ERP, or a self-clearing sync
//                   condition). No inline affordance by design.
//   none          — a def with NO actionType: nothing for the merchant to do
//                   anywhere, so no affordance. Whether a card needs attention
//                   at all (bell dot, needs-attention count) is the backend's
//                   `scored` on the issue registry, and the two agree by
//                   contract: exactly the unscored issues have no actionType
//                   (api/test_data_health_registry.py FRONTEND_NO_ACTION_KEYS).
// ---------------------------------------------------------------------------
const RESOLUTION_BY_ACTION = {
  'assign-location': 'inline',
  'dropdown-edit': 'inline',
  'price-edit': 'inline',
  'text-edit': 'inline',
  'category-edit': 'inline',
  'quantity-edit': 'inline',
  'price-below-cost': 'inline',
  'toggle': 'action',
  'activate': 'action',
  'create-in-channel': 'action',
  'delete-row': 'action',
  'erp-leftover': 'action',
  'duplicate-review': 'bespoke',
  'supplier-link': 'bespoke',
  'order-constraints-fix': 'deep-link',
  'dos-target-fix': 'deep-link',
  'page-link': 'deep-link',
  'external': 'external',
};

/** Resolution class for an issue's actionType: 'none' for a def without
 *  one, or null if unclassified (the invariant test fails loudly on null — a
 *  new actionType must declare how it resolves). */
export function resolutionOf(actionType) {
  if (actionType === undefined) return 'none';
  return RESOLUTION_BY_ACTION[actionType] ?? null;
}

/** actionTypes whose fix is an in-grid cell edit (batch save). Derived from
 *  the taxonomy so it can't drift from `resolutionOf`. */
export const BATCH_EDITABLE_TYPES = new Set(
  Object.keys(RESOLUTION_BY_ACTION).filter((a) => RESOLUTION_BY_ACTION[a] === 'inline'),
);

// ---------------------------------------------------------------------------
// Issue definitions — tone, icon, and resolution wiring. `icon` is a key into
// ICON_MAP in DataHealth.jsx (kept there — it needs the Phosphor imports).
// ---------------------------------------------------------------------------
export const ISSUE_DEFS = {
  // Assign a location to an item that has none (invisible in the Items grid +
  // unplannable). Routes to ItemLocationViewSet.bulk-create (a create, not a
  // bulk-update — no ItemLocation row exists yet to patch). Also self-heals
  // when a connector next reports a placement for the item.
  missing_location:    { actionType: 'assign-location', viewset: 'item-locations', optionsUrl: '/api/locations/', optionId: 'location_id', optionLabel: 'location_name', tone: 'critical', icon: 'mapPin' },
  // These rows are items with NO supplier at all. The fix ASSIGNS one of the
  // store's suppliers (whole list via optionsUrl), which auto-becomes the item's
  // default — writing default_supplier_id births the first relation as default.
  // The "no suppliers at all" alert uses a lightweight has-suppliers count.
  missing_supplier:    { actionType: 'dropdown-edit', viewset: 'items', fieldName: 'default_supplier_id', optionsUrl: '/api/suppliers/', optionId: 'supplier_id', optionLabel: 'supplier_name', tone: 'critical', icon: 'handshake' },
  missing_cost:        { actionType: 'price-edit', viewset: 'items', fieldName: 'current_cost_price_supplier_ccy', tone: 'critical', icon: 'coins' },
  // A forecastable placement with NO inventory record — the PO engine holds
  // it out of recommendations (exclusion_reasons.missing_inventory)
  // because "no record" is not "zero stock". Entering a count (0 included)
  // routes through InventoryViewSet.bulk_update, which CREATES the missing
  // row; the current-count column renders blank, never a substituted 0.
  missing_inventory:   { actionType: 'quantity-edit', viewset: 'inventory', fieldName: 'inventory_on_hand', tone: 'critical', icon: 'package' },
  missing_forecast:    { tone: 'info', icon: 'chartLineUp' },

  missing_sell_price:  { actionType: 'price-edit', viewset: 'items', fieldName: 'current_sell_price', tone: 'warning', icon: 'currency' },
  // NO fieldName: the field a category write lands on is the store's to
  // declare, not this table's (`category_editing.write_field` — a taxonomy
  // store writes `product_category_id`, a product-type store writes
  // `item_type` and the backend derives the category from it). The action is
  // the same on both: pick a category in the grid. `categoryWrite` names the
  // field at save time, so nothing here has to guess which store it is on.
  missing_product_category: { actionType: 'category-edit', viewset: 'items', tone: 'warning', icon: 'package' },
  missing_sku:         { actionType: 'text-edit', viewset: 'items', fieldName: 'shopify_sku', tone: 'warning', icon: 'barcode' },
  // Both sides of a shared SKU are rows here, and either one resolves the
  // pair — so the fix is the same inline SKU edit `missing_sku` offers, not a
  // bespoke pair-merge card. (`duplicate_items` IS a merge, because there the
  // two rows are one real product; here they are two products wearing one
  // code.)
  duplicate_sku:       { actionType: 'text-edit', viewset: 'items', fieldName: 'shopify_sku', tone: 'warning', icon: 'copy' },

  tracking_off:        { actionType: 'toggle', viewset: 'items', fieldName: 'inventory_tracked', fixedValue: true, tone: 'info', icon: 'eyeSlash' },
  overselling:         { actionType: 'toggle', viewset: 'items', fieldName: 'allows_overselling', fixedValue: false, tone: 'info', icon: 'package' },
  not_purchasable:     { actionType: 'toggle', viewset: 'item-locations', fieldName: 'purchasable_flag', fixedValue: true, tone: 'info', icon: 'prohibit' },
  digital_item:        { actionType: 'toggle', viewset: 'items', fieldName: 'requires_shipping', fixedValue: true, tone: 'info', icon: 'truck' },
  bundle_item:         { tone: 'info', icon: 'cube' },

  negative_inventory:  { actionType: 'quantity-edit', viewset: 'inventory', fieldName: 'inventory_on_hand', tone: 'critical', icon: 'minusCircle' },
  inactive_with_stock: { actionType: 'activate', viewset: 'items', fieldName: 'item_status', fixedValue: 1, tone: 'warning', icon: 'archive' },
  price_below_cost:    { actionType: 'price-below-cost', viewset: 'items', tone: 'critical', icon: 'trendDown' },

  // ERP connector health. `external` signals resolve in the ERP (re-add/delete)
  // or on another Setup screen — never a bulk fix here; each card's copy names
  // where. The one exception is the channel-product create, a real per-row
  // action:
  // ERP-imported items with no product in the sales channel (the merchant's
  // own ERP→channel sync failed). Per-row action creates the product in the
  // channel and links the merchant to it; viewset action = ItemViewSet
  // create-in-channel. Manexys pushes the fields it holds (title, category,
  // price, SKU, cost) — the merchant adds images etc. via the link.
  erp_item_missing_channel_product: { actionType: 'create-in-channel', viewset: 'items', tone: 'warning', icon: 'package' },
  erp_item_no_channel:   { actionType: 'external', tone: 'warning', icon: 'warningCircle' },
  erp_orphaned_item:     { actionType: 'external', tone: 'info', icon: 'cube' },
  erp_orphaned_supplier: { actionType: 'external', tone: 'info', icon: 'handshake' },
  // Left over after an ERP disconnect: ERP-only rows kept (not deleted) because
  // they anchored sales / PO / stock history. Per-row Keep (adopt as native —
  // clears the erp_detached origin) or Retire (soft-delete, warning first when
  // stock remains) via the owning viewset's keep-native / retire actions.
  erp_leftover_item:     { actionType: 'erp-leftover', viewset: 'items', tone: 'info', icon: 'cube' },
  erp_leftover_location: { actionType: 'erp-leftover', viewset: 'locations', tone: 'info', icon: 'mapPin' },
  // Manexys-native suppliers with no ERP link — blocks PO creation in the ERP
  // until linked. Per-row "Link to Exact" opens LinkSupplierToExactDialog (the
  // same merge/adopt flow as Settings → Suppliers) right here; the connector
  // link endpoint is the write, so this is bespoke, not a bulk-update.
  supplier_not_linked_to_erp: { actionType: 'supplier-link', tone: 'warning', icon: 'handshake' },
  // The ERP prices this item in a currency that differs from its supplier's
  // default currency, so cost can't be confirmed and is skipped on sync.
  // External — the merchant aligns the supplier's currency in Supplier Setup;
  // the signal self-clears once they match.
  cost_currency_mismatch: { actionType: 'external', tone: 'warning', icon: 'coins' },
  // A supplier relation whose order rules can never be satisfied together — the
  // smallest orderable quantity (rounded up to the order multiple, at least the
  // MOQ) exceeds the max order quantity. The three fields interact and are edited
  // together on the Supplier catalog screen, so a per-row "Fix order rules" button
  // deep-links there (filtered to the item) rather than editing inline. The
  // engine caps to the max and flags it, so this only degrades accuracy.
  contradictory_order_constraints: { actionType: 'order-constraints-fix', tone: 'warning', icon: 'warningCircle' },
  // A Days of supply target shorter than the default supplier's review cycle
  // (the cycle alone, not lead time + cycle — api.services.data_health says
  // why), so the placement runs dry before the next delivery (the engine
  // never clamps the target up). The target cascades Store → Supplier →
  // Item-location, so the row names the level each number is set at and a
  // per-row button deep-links to where the fix belongs — Item setup, Supplier
  // setup or Store settings (datahealth/dosTargetFix.js; the backend decides
  // the level) — rather than editing one level of the chain blind from here.
  dos_target_below_cycle: { actionType: 'dos-target-fix', tone: 'warning', icon: 'calendar' },
  // A placement no connector (or the merchant) still asserts. The per-row
  // Remove deletes the ItemLocation via a guarded destroy (refused with 409 if
  // it still holds stock / an inbound PO / an open transfer). Never
  // auto-deleted — the merchant confirms each removal.
  stale_placement:       { actionType: 'delete-row', viewset: 'item-locations', tone: 'info', icon: 'mapPin' },
  // Cross-origin duplicate items, merged only on merchant confirmation (never
  // automatically). Reviewed via the bespoke pair UI (DuplicateReview) —
  // keep/remove/swap/merge/"not the same" — not the generic AG-Grid detail flow;
  // its writes are the identity API, not a bulk-update. Surfaced here for the
  // count + entry point.
  duplicate_items:       { actionType: 'duplicate-review', tone: 'warning', icon: 'copy' },
};

/**
 * The def for an issue, or undefined for an unknown key.
 *
 * @param {string} issueKey
 * @returns {object|undefined}
 */
export function issueDef(issueKey) {
  return ISSUE_DEFS[issueKey];
}
